import { Chess } from 'chess.js';

export type TrainingColor = 'w' | 'b';

export type TrainingStep = {
  color: TrainingColor;
  move: string;
};

export type CompiledTrainingSequence = {
  steps: TrainingStep[];
  prefixFens: string[];
};

export type CompiledTrainingSequenceValidation =
  | { valid: true; steps: TrainingStep[]; prefixFens: string[] }
  | { valid: false; message: string };

function forceTurn(game: Chess, color: TrainingColor) {
  const parts = game.fen().split(' ');
  parts[1] = color;
  parts[4] = '0';
  parts[5] = '1';
  game.load(parts.join(' '), { skipValidation: true });
}

export function parseTrainingSequence(sequence: string): TrainingStep[] {
  if (!sequence) return [];

  return sequence.split(/,\s*/).filter((raw) => raw.trim().length > 0).map((raw) => {
    const normalized = raw.trim();
    const separator = normalized.indexOf(':');
    if (separator >= 0) {
      const color = normalized.slice(0, separator).trim();
      return {
        color: color === 'b' ? 'b' : 'w',
        move: normalized.slice(separator + 1).trim()
      };
    }

    return { color: 'w', move: normalized };
  });
}

export function compileTrainingSequence(
  initialFen: string,
  sequence: string
): CompiledTrainingSequenceValidation {
  const text = (sequence || '').trim();
  if (!text) {
    const initialColor: TrainingColor =
      ((initialFen || '').split(/\s+/)[1] === 'b' ? 'b' : 'w');
    const game = initialGame(initialFen, initialColor);
    return { valid: true, steps: [], prefixFens: [game.fen()] };
  }

  const rawSteps = text.split(/,\s*/).filter((raw) => raw.trim().length > 0);
  for (const raw of rawSteps) {
    const normalized = raw.trim();
    const separator = normalized.indexOf(':');

    if (separator >= 0) {
      const color = normalized.slice(0, separator).trim();
      const move = normalized.slice(separator + 1).trim();
      if (color !== 'w' && color !== 'b') {
        return { valid: false, message: 'неизвестный цвет хода' };
      }
      if (!move) {
        return { valid: false, message: 'пустой ход' };
      }
    }

    const move = separator >= 0 ? normalized.slice(separator + 1).trim() : normalized;
    if (move.length > 64) {
      return { valid: false, message: 'ход слишком длинный' };
    }
  }

  const steps = parseTrainingSequence(text);

  try {
    const initialColor: TrainingColor =
      steps[0]?.color || ((initialFen || '').split(/\s+/)[1] === 'b' ? 'b' : 'w');
    const game = initialGame(initialFen, initialColor);
    const prefixFens = [game.fen()];

    for (const step of steps) {
      playTrainingStep(game, step);
      prefixFens.push(game.fen());
    }

    return { valid: true, steps, prefixFens };
  } catch {
    return {
      valid: false,
      message: 'последовательность невозможно воспроизвести из начальной позиции'
    };
  }
}

export function validateTrainingSequence(initialFen: string, sequence: string):
  { valid: true; steps: TrainingStep[] } |
  { valid: false; message: string } {
  const validation = compileTrainingSequence(initialFen, sequence);
  if (!validation.valid) return validation;
  return { valid: true, steps: validation.steps };
}

export function cleanTrainingSan(san: string, game: Chess): string {
  if (!san.includes('#')) return san;

  const placement = game.fen().split(' ')[0];
  if (!placement.includes('K') || !placement.includes('k')) {
    return san.replace('#', '');
  }

  return game.isCheckmate() ? san : san.replace('#', '');
}

function initialGame(initialFen: string, color: TrainingColor) {
  const game = new Chess();
  const fen = (initialFen || '').trim();
  if (fen) {
    try {
      game.load(fen, { skipValidation: true });
      forceTurn(game, color);
      return game;
    } catch {
      // Fall through to a structurally useful placement-only FEN.
    }
  }

  const placement = fen.split(' ')[0] || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR';
  game.load(placement + ' ' + color + ' KQkq - 0 1', { skipValidation: true });
  return game;
}

export function replayTrainingPrefix(initialFen: string, steps: TrainingStep[], count: number): Chess {
  const initialColor: TrainingColor =
    steps[0]?.color || ((initialFen || '').split(/\s+/)[1] === 'b' ? 'b' : 'w');

  const game = initialGame(initialFen, initialColor);

  for (let index = 0; index < count; index += 1) {
    const step = steps[index];
    if (!step) break;
    playTrainingStep(game, step);
  }

  return game;
}

export function playTrainingStep(game: Chess, step: TrainingStep) {
  forceTurn(game, step.color);
  const moveText = step.move.trim();

  if (/^O-O(?:-O)?$/.test(moveText)) {
    const from = step.color === 'w' ? 'e1' : 'e8';
    const to = moveText === 'O-O'
      ? (step.color === 'w' ? 'g1' : 'g8')
      : (step.color === 'w' ? 'c1' : 'c8');
    return game.move({ from, to });
  }

  if (/^[a-h][1-8]-[a-h][1-8](?:-[qrbn])?$/.test(moveText)) {
    const [from, to, promotion] = moveText.split('-');
    return promotion
      ? game.move({ from, to, promotion: promotion as 'q' | 'r' | 'b' | 'n' })
      : game.move({ from, to });
  }

  return game.move(moveText);
}

export function playTrainingMove(game: Chess, color: TrainingColor, moveText: string) {
  forceTurn(game, color);
  moveText = moveText.trim();

  if (/^[a-h][1-8]-[a-h][1-8](?:-[qrbn])?$/.test(moveText)) {
    const [from, to, promotion] = moveText.split('-');
    return promotion
      ? game.move({ from, to, promotion: promotion as 'q' | 'r' | 'b' | 'n' })
      : game.move({ from, to });
  }

  return game.move(moveText);
}

export function trainingMoveMatches(expected: string, actualSan: string, coordinateMove: string) {
  const cleanExpected = expected.replace(/[+#]/g, '');
  const cleanActual = actualSan.replace(/[+#]/g, '');
  return cleanExpected === cleanActual || expected === coordinateMove;
}
